import { useEffect, useState } from 'react';
import PageHeader from '../components/PageHeader';
import { Card, SaveBar, MissingConfigBanner } from '../components/Form';
import { useConfigDoc, useWriteConfig } from '../lib/configHooks';

type CatalogMap = Record<string, number>;

function MapEditor({
  rows,
  onChange,
  keyLabel,
  valueLabel,
  valueStep,
}: {
  rows: Array<{ key: string; value: number }>;
  onChange: (rows: Array<{ key: string; value: number }>) => void;
  keyLabel: string;
  valueLabel: string;
  valueStep: number;
}) {
  const update = (i: number, patch: Partial<{ key: string; value: number }>) => {
    const next = [...rows];
    next[i] = { ...next[i], ...patch };
    onChange(next);
  };
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-12 gap-2 text-xs font-medium uppercase tracking-wide text-slate-500">
        <div className="col-span-6">{keyLabel}</div>
        <div className="col-span-5">{valueLabel}</div>
        <div className="col-span-1" />
      </div>
      {rows.map((r, i) => (
        <div key={i} className="grid grid-cols-12 gap-2">
          <input
            type="text"
            value={r.key}
            onChange={(e) => update(i, { key: e.target.value })}
            className="col-span-6 rounded border border-slate-300 px-2 py-1.5 text-sm"
          />
          <input
            type="number"
            step={valueStep}
            value={Number.isFinite(r.value) ? r.value : ''}
            onChange={(e) =>
              update(i, { value: parseFloat(e.target.value) })
            }
            className="col-span-5 rounded border border-slate-300 px-2 py-1.5 text-sm"
          />
          <button
            type="button"
            onClick={() => onChange(rows.filter((_, j) => j !== i))}
            className="col-span-1 rounded border border-slate-300 bg-white text-sm text-slate-500 hover:bg-slate-50"
            aria-label="Remove row"
          >
            ×
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={() => onChange([...rows, { key: '', value: 0 }])}
        className="mt-2 rounded border border-dashed border-slate-300 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50"
      >
        + Add row
      </button>
    </div>
  );
}

function toRows(map: CatalogMap | undefined) {
  return Object.entries(map || {}).map(([key, value]) => ({ key, value }));
}
function fromRows(rows: Array<{ key: string; value: number }>): CatalogMap {
  const out: CatalogMap = {};
  for (const r of rows) {
    if (r.key.trim() && Number.isFinite(r.value)) out[r.key.trim()] = r.value;
  }
  return out;
}

export default function CatalogsPage() {
  const vehiclesDoc = useConfigDoc<{ multipliers?: CatalogMap }>(
    'catalogs_vehicles'
  );
  const equipmentDoc = useConfigDoc<{ prices?: CatalogMap }>(
    'catalogs_equipment'
  );
  const writeVehicles = useWriteConfig('catalogs_vehicles');
  const writeEquipment = useWriteConfig('catalogs_equipment');

  const [vehicles, setVehicles] = useState<
    Array<{ key: string; value: number }>
  >([]);
  const [equipment, setEquipment] = useState<
    Array<{ key: string; value: number }>
  >([]);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!dirty) {
      setVehicles(toRows(vehiclesDoc.data?.multipliers));
      setEquipment(toRows(equipmentDoc.data?.prices));
    }
  }, [vehiclesDoc.data, equipmentDoc.data, dirty]);

  const onSave = async () => {
    await Promise.all([
      writeVehicles.mutateAsync({ multipliers: fromRows(vehicles) }),
      writeEquipment.mutateAsync({ prices: fromRows(equipment) }),
    ]);
    setDirty(false);
  };

  return (
    <>
      <PageHeader
        title="Catalogs"
        subtitle="Vehicle multipliers and equipment add-on prices."
      />
      {((vehiclesDoc.data && Object.keys(vehiclesDoc.data).length === 0) ||
        (equipmentDoc.data && Object.keys(equipmentDoc.data).length === 0)) && (
        <MissingConfigBanner docIds={['catalogs_vehicles', 'catalogs_equipment']} />
      )}
      <Card title="Vehicle multipliers">
        <p className="mb-3 text-xs text-slate-500">
          Multiplier applied to base fare per vehicle type (e.g. <code>truck</code> →{' '}
          <code>1.5</code>).
        </p>
        <MapEditor
          rows={vehicles}
          onChange={(r) => {
            setVehicles(r);
            setDirty(true);
          }}
          keyLabel="Vehicle type"
          valueLabel="Multiplier"
          valueStep={0.05}
        />
      </Card>
      <Card title="Equipment prices (¢)">
        <p className="mb-3 text-xs text-slate-500">
          Per-item add-on charge in cents (e.g. <code>dolly</code> →{' '}
          <code>500</code> = $5.00).
        </p>
        <MapEditor
          rows={equipment}
          onChange={(r) => {
            setEquipment(r);
            setDirty(true);
          }}
          keyLabel="Equipment"
          valueLabel="Price (¢)"
          valueStep={1}
        />
      </Card>
      <SaveBar
        dirty={dirty}
        saving={writeVehicles.isPending || writeEquipment.isPending}
        onSave={onSave}
        onReset={() => {
          setVehicles(toRows(vehiclesDoc.data?.multipliers));
          setEquipment(toRows(equipmentDoc.data?.prices));
          setDirty(false);
        }}
      />
    </>
  );
}
